import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Repeat2, Type, LayoutTemplate, Search, Loader2, Copy, Check,
  BookOpen, ListOrdered, BarChart2, Columns, Compass, Star, HelpCircle, Lightbulb,
  ChevronRight, ArrowLeft, Sparkles, X, Network, Share2, Twitter, Linkedin, Instagram, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'repurpose', label: 'Repurpose', icon: Repeat2 },
  { id: 'headlines', label: 'Headlines', icon: Type },
  { id: 'templates', label: 'Templates', icon: LayoutTemplate },
  { id: 'competitor', label: 'Competitor', icon: Search },
  { id: 'clusters', label: 'SEO Clusters', icon: Network },
  { id: 'social', label: 'Social Posts', icon: Share2 },
];

const TEMPLATE_ICONS = {
  'book-open': BookOpen, 'list-ordered': ListOrdered, 'bar-chart-2': BarChart2,
  'columns': Columns, 'compass': Compass, 'star': Star, 'help-circle': HelpCircle, 'lightbulb': Lightbulb
};

const ContentTools = () => {
  const [activeTab, setActiveTab] = useState('repurpose');

  return (
    <div className="fade-in" data-testid="content-tools-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl mb-1">Content Tools</h1>
        <p className="text-sm text-[var(--color-muted)]">AI-powered tools to maximize your content's reach and impact.</p>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 p-1 rounded-2xl bg-[var(--color-surface)] mb-6 w-fit" data-testid="tools-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[var(--color-surface-elevated)] text-[var(--color-charcoal)] shadow-sm'
                  : 'text-[var(--color-muted)] hover:text-[var(--color-charcoal)]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === 'repurpose' && <RepurposeTab />}
      {activeTab === 'headlines' && <HeadlineTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'competitor' && <CompetitorTab />}
      {activeTab === 'clusters' && <ClustersPanel />}
      {activeTab === 'social' && <SocialPostsPanel />}
    </div>
  );
};

// ============== REPURPOSE TAB ==============
const RepurposeTab = () => {
  const { getAuthHeaders } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [repurposing, setRepurposing] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    axios.get(`${API}/articles`, { headers: getAuthHeaders() })
      .then(r => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRepurpose = async () => {
    if (!selectedArticle) return;
    setRepurposing(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/tools/repurpose`, {
        article_id: selectedArticle.id,
        platforms: ['facebook', 'instagram', 'linkedin', 'twitter', 'email']
      }, { headers: getAuthHeaders() });
      setResults(response.data.results);
    } catch (err) {
      alert(err.response?.data?.detail || 'Repurposing failed');
    } finally { setRepurposing(false); }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const platformLabels = {
    facebook: 'Facebook Post',
    instagram: 'Instagram Caption',
    linkedin: 'LinkedIn Post',
    twitter: 'Twitter/X Thread',
    email: 'Email Newsletter'
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>;

  return (
    <div data-testid="repurpose-tab">
      {!results ? (
        <>
          <p className="text-sm text-[var(--color-muted)] mb-4">Select an article to transform into platform-specific content.</p>
          <div className="space-y-2 mb-6 max-h-[400px] overflow-y-auto">
            {articles.length === 0 ? (
              <div className="card p-8 text-center">
                <p className="text-sm text-[var(--color-muted)]">No articles yet. Create one first to repurpose.</p>
              </div>
            ) : articles.map(article => (
              <div
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className={`card p-4 cursor-pointer transition-all ${
                  selectedArticle?.id === article.id
                    ? 'ring-2 ring-[var(--color-coral)] bg-[rgba(232,115,92,0.03)]'
                    : 'hover:bg-[var(--color-surface)]'
                }`}
                data-testid={`repurpose-article-${article.id}`}
              >
                <h3 className="font-display text-sm font-medium">{article.title || 'Untitled'}</h3>
                <p className="text-[10px] text-[var(--color-muted)] mt-0.5">
                  {article.status} &middot; {new Date(article.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
          <button
            onClick={handleRepurpose}
            disabled={!selectedArticle || repurposing}
            className="btn btn-primary"
            data-testid="repurpose-btn"
          >
            {repurposing ? <><Loader2 className="w-4 h-4 animate-spin" /> Repurposing...</> : <><Repeat2 className="w-4 h-4" /> Repurpose into 5 Formats</>}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setResults(null)} className="btn btn-ghost p-2"><ArrowLeft className="w-4 h-4" /></button>
            <div>
              <h2 className="font-display text-base font-medium">{selectedArticle?.title}</h2>
              <p className="text-[10px] text-[var(--color-muted)]">Repurposed into {Object.keys(results).length} formats</p>
            </div>
          </div>
          <div className="space-y-4">
            {Object.entries(results).map(([platform, content]) => (
              <div key={platform} className="card p-5" data-testid={`result-${platform}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-sm font-medium">{platformLabels[platform] || platform}</h3>
                  <button
                    onClick={() => copyToClipboard(content, platform)}
                    className="btn btn-ghost p-1.5 text-[10px] flex items-center gap-1"
                    data-testid={`copy-${platform}`}
                  >
                    {copied === platform ? <><Check className="w-3 h-3 text-green-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <div className="text-xs text-[var(--color-charcoal)] whitespace-pre-wrap leading-relaxed bg-[var(--color-surface)] rounded-xl p-4 max-h-[200px] overflow-y-auto">
                  {content}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============== HEADLINE ANALYZER TAB ==============
const HeadlineTab = () => {
  const { getAuthHeaders } = useAuth();
  const [headline, setHeadline] = useState('');
  const [industry, setIndustry] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!headline.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const response = await axios.post(`${API}/tools/analyze-headline`, {
        headline: headline.trim(),
        industry
      }, { headers: getAuthHeaders() });
      setAnalysis(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div data-testid="headline-tab">
      <form onSubmit={handleAnalyze} className="space-y-4 mb-6">
        <div>
          <label className="block text-xs font-medium mb-1.5">Enter a Headline to Analyze *</label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            className="input text-lg"
            placeholder="e.g., 10 Proven Ways to Boost Your SEO in 2026"
            required
            data-testid="headline-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5">Industry (optional)</label>
          <input
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="input"
            placeholder="e.g., SaaS, E-commerce, Health"
          />
        </div>
        <button type="submit" disabled={analyzing || !headline.trim()} className="btn btn-primary" data-testid="analyze-headline-btn">
          {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <><Sparkles className="w-4 h-4" /> Analyze Headline</>}
        </button>
      </form>

      {analysis && !analysis.error && (
        <div className="space-y-5" data-testid="headline-results">
          {/* Overall Score */}
          <div className="card p-6 text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${getScoreColor(analysis.overall_score)}`}>
              {analysis.overall_score}
            </div>
            <p className="text-sm font-medium mt-3">Overall Score</p>
            <p className="text-[10px] text-[var(--color-muted)]">{analysis.word_count} words &middot; {analysis.character_count} characters</p>
          </div>

          {/* Score Breakdown */}
          {analysis.scores && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(analysis.scores).map(([key, val]) => (
                <div key={key} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium capitalize">{key.replace('_', ' ')}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getScoreColor(val.score)}`}>{val.score}</span>
                  </div>
                  <p className="text-[10px] text-[var(--color-muted)]">{val.feedback}</p>
                </div>
              ))}
            </div>
          )}

          {/* Power Words */}
          {analysis.power_words?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-2">Power Words Detected</h3>
              <div className="flex flex-wrap gap-2">
                {analysis.power_words.map((word, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[rgba(232,115,92,0.08)] text-[var(--color-coral)] font-medium">{word}</span>
                ))}
              </div>
            </div>
          )}

          {/* Improvements */}
          {analysis.improvements?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Suggestions to Improve</h3>
              <ul className="space-y-2">
                {analysis.improvements.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--color-charcoal)]">
                    <ChevronRight className="w-3 h-3 mt-0.5 text-[var(--color-coral)] flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Alternatives */}
          {analysis.alternatives?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Alternative Headlines</h3>
              <div className="space-y-2">
                {analysis.alternatives.map((alt, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[var(--color-surface)] text-xs">
                    <span className="flex-1">{alt}</span>
                    <button
                      onClick={() => { setHeadline(alt); setAnalysis(null); }}
                      className="btn btn-ghost p-1 text-[10px] ml-3"
                    >
                      Try this
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== TEMPLATES TAB ==============
const TemplatesTab = () => {
  const { getAuthHeaders, refreshSubscription } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tools/templates`, { headers: getAuthHeaders() })
      .then(r => setTemplates(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selected || !topic.trim()) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/tools/generate-from-template`, {
        template_id: selected.id,
        topic: topic.trim(),
        keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
        tone,
        length,
      }, { headers: getAuthHeaders() });
      setGeneratedArticle(response.data.article);
      await refreshSubscription();
    } catch (err) {
      alert(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>;

  if (generatedArticle) {
    return (
      <div data-testid="template-result">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => { setGeneratedArticle(null); setSelected(null); setTopic(''); }} className="btn btn-ghost p-2"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <h2 className="font-display text-base font-medium">{generatedArticle.title}</h2>
            <p className="text-[10px] text-[var(--color-muted)]">Generated from {selected?.name} template &middot; Saved as draft</p>
          </div>
        </div>
        <div className="card p-6">
          <div className="prose prose-sm max-w-none text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: generatedArticle.content }} />
        </div>
      </div>
    );
  }

  if (selected) {
    const Icon = TEMPLATE_ICONS[selected.icon] || LayoutTemplate;
    return (
      <div data-testid="template-form">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setSelected(null)} className="btn btn-ghost p-2"><ArrowLeft className="w-4 h-4" /></button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[rgba(232,115,92,0.08)]">
              <Icon className="w-4 h-4 text-[var(--color-coral)]" />
            </div>
            <div>
              <h2 className="font-display text-base font-medium">{selected.name}</h2>
              <p className="text-[10px] text-[var(--color-muted)]">{selected.structure}</p>
            </div>
          </div>
        </div>
        <form onSubmit={handleGenerate} className="card p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5">Topic / Title *</label>
            <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
              className="input" placeholder={selected.example_title} required data-testid="template-topic-input" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Keywords (comma separated)</label>
            <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)}
              className="input" placeholder="SEO, content marketing, small business" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="input">
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="authoritative">Authoritative</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Length</label>
              <select value={length} onChange={(e) => setLength(e.target.value)} className="input">
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={generating || !topic.trim()} className="btn btn-primary" data-testid="template-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate {selected.name}</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="templates-tab">
      <p className="text-sm text-[var(--color-muted)] mb-4">Choose a proven article template to structure your content.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {templates.map(template => {
          const Icon = TEMPLATE_ICONS[template.icon] || LayoutTemplate;
          return (
            <div
              key={template.id}
              onClick={() => setSelected(template)}
              className="card p-5 cursor-pointer transition-all hover:shadow-md group"
              data-testid={`template-${template.id}`}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)] mb-3 group-hover:bg-[rgba(232,115,92,0.08)] transition-colors">
                <Icon className="w-4 h-4 text-[var(--color-charcoal)] group-hover:text-[var(--color-coral)] transition-colors" />
              </div>
              <h3 className="font-display text-sm font-medium mb-1">{template.name}</h3>
              <p className="text-[10px] text-[var(--color-muted)] leading-relaxed">{template.description}</p>
              <p className="text-[9px] text-[var(--color-coral)] font-medium mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Use template <ChevronRight className="w-3 h-3" />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============== COMPETITOR ANALYSIS TAB ==============
const CompetitorTab = () => {
  const { getAuthHeaders } = useAuth();
  const [url, setUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [topics, setTopics] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const response = await axios.post(`${API}/tools/competitor-analysis`, {
        competitor_url: url.trim(),
        your_industry: industry,
        your_topics: topics.split(',').map(t => t.trim()).filter(Boolean)
      }, { headers: getAuthHeaders() });
      setAnalysis(response.data.analysis);
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const priorityColor = (p) => {
    if (p === 'high') return 'bg-red-50 text-red-700';
    if (p === 'medium') return 'bg-yellow-50 text-yellow-700';
    return 'bg-green-50 text-green-700';
  };

  return (
    <div data-testid="competitor-tab">
      <form onSubmit={handleAnalyze} className="space-y-4 mb-6">
        <div>
          <label className="block text-xs font-medium mb-1.5">Competitor URL *</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="input"
            placeholder="https://competitor.com"
            required
            data-testid="competitor-url-input"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5">Your Industry</label>
            <input type="text" value={industry} onChange={(e) => setIndustry(e.target.value)}
              className="input" placeholder="e.g., SaaS, E-commerce" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Topics You Cover (comma separated)</label>
            <input type="text" value={topics} onChange={(e) => setTopics(e.target.value)}
              className="input" placeholder="SEO, content marketing, automation" />
          </div>
        </div>
        <button type="submit" disabled={analyzing || !url.trim()} className="btn btn-primary" data-testid="competitor-analyze-btn">
          {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <><Search className="w-4 h-4" /> Analyze Competitor</>}
        </button>
      </form>

      {analysis && !analysis.error && (
        <div className="space-y-5" data-testid="competitor-results">
          {/* Summary */}
          <div className="card p-5">
            <h3 className="font-display text-sm font-medium mb-2">{analysis.competitor_name || 'Competitor'}</h3>
            <p className="text-xs text-[var(--color-muted)]">{analysis.competitor_summary}</p>
          </div>

          {/* Content Gaps */}
          {analysis.content_gaps?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Content Gaps to Exploit</h3>
              <div className="space-y-3">
                {analysis.content_gaps.map((gap, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[var(--color-surface)]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{gap.topic}</span>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${priorityColor(gap.priority)}`}>{gap.priority}</span>
                    </div>
                    <p className="text-[10px] text-[var(--color-muted)]">{gap.opportunity}</p>
                    {gap.suggested_title && (
                      <p className="text-[10px] text-[var(--color-coral)] mt-1 font-medium">Article idea: {gap.suggested_title}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keyword Opportunities */}
          {analysis.keyword_opportunities?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Keyword Opportunities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {analysis.keyword_opportunities.map((kw, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[var(--color-surface)] flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">{kw.keyword}</span>
                      <p className="text-[9px] text-[var(--color-muted)]">{kw.potential}</p>
                    </div>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${priorityColor(kw.difficulty === 'low' ? 'high' : kw.difficulty === 'high' ? 'low' : 'medium')}`}>
                      {kw.difficulty} difficulty
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategy Recommendations */}
          {analysis.content_strategy_recommendations?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Strategy Recommendations</h3>
              <ul className="space-y-2">
                {analysis.content_strategy_recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <ChevronRight className="w-3 h-3 mt-0.5 text-[var(--color-coral)] flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quick Wins */}
          {analysis.quick_wins?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-display text-sm font-medium mb-3">Quick Win Articles</h3>
              <div className="space-y-2">
                {analysis.quick_wins.map((win, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-surface)]">
                    <Sparkles className="w-4 h-4 text-[var(--color-coral)] flex-shrink-0" />
                    <span className="text-xs">{win}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== CLUSTERS PANEL ==============
const ClustersPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [topic, setTopic] = useState('');
  const [industry, setIndustry] = useState('');
  const [audience, setAudience] = useState('');
  const [generating, setGenerating] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [activeCluster, setActiveCluster] = useState(null);

  useEffect(() => { fetchClusters(); }, []);

  const fetchClusters = async () => {
    try {
      const res = await axios.get(`${API}/tools/clusters`, { headers: getAuthHeaders() });
      setClusters(res.data);
    } catch {}
  };

  const generate = async () => {
    if (!topic.trim() || generating) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/tools/clusters/generate`, {
        pillar_topic: topic, industry, target_audience: audience
      }, { headers: getAuthHeaders() });
      setActiveCluster({ ...res.data.cluster, id: res.data.id, pillar_topic: topic });
      setTopic('');
      fetchClusters();
    } catch (err) {
      alert(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const deleteCluster = async (id) => {
    try {
      await axios.delete(`${API}/tools/clusters/${id}`, { headers: getAuthHeaders() });
      fetchClusters();
      if (activeCluster?.id === id) setActiveCluster(null);
    } catch {}
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="clusters-panel">
      <div className="lg:col-span-1 space-y-4">
        <div className="card p-5">
          <h3 className="font-display text-sm font-medium mb-3">Generate Cluster Strategy</h3>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Pillar topic (e.g., 'Content Marketing for SaaS')" className="input w-full mb-2 text-xs" data-testid="cluster-topic-input" />
          <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry (optional)" className="input w-full mb-2 text-xs" />
          <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Target audience (optional)" className="input w-full mb-3 text-xs" />
          <button onClick={generate} disabled={generating || !topic.trim()} className="btn btn-primary w-full text-xs" data-testid="generate-cluster-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Network className="w-4 h-4" /> Generate (2.5 credits)</>}
          </button>
        </div>
        <div className="card p-4">
          <h4 className="font-display text-xs font-medium mb-3">Saved Clusters ({clusters.length}/5)</h4>
          <div className="space-y-2">
            {clusters.map((c) => (
              <div key={c.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-[var(--color-surface)] cursor-pointer hover:bg-[var(--color-border-subtle)] transition-colors" onClick={() => setActiveCluster({ ...c.cluster, id: c.id, pillar_topic: c.pillar_topic })} data-testid={`cluster-item-${c.id}`}>
                <Network className="w-3.5 h-3.5 text-[var(--color-coral)] flex-shrink-0" />
                <span className="text-xs truncate flex-1">{c.pillar_topic}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteCluster(c.id); }} className="p-1 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {clusters.length === 0 && <p className="text-[10px] text-[var(--color-muted)] text-center py-3">No clusters yet. Generate one above.</p>}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2" data-testid="cluster-detail">
        {activeCluster ? (
          <div className="card p-6">
            {activeCluster.pillar && (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-coral)]/10 flex items-center justify-center"><Network className="w-5 h-5 text-[var(--color-coral)]" /></div>
                  <div>
                    <h3 className="font-serif text-lg">{activeCluster.pillar.title}</h3>
                    <p className="text-[10px] text-[var(--color-muted)]">{activeCluster.pillar.target_keyword} &middot; ~{activeCluster.pillar.word_count} words &middot; Traffic: {activeCluster.estimated_traffic_potential}</p>
                  </div>
                </div>
                <p className="text-xs text-[var(--color-muted)] mb-5">{activeCluster.pillar.description}</p>

                <h4 className="font-display text-xs font-medium mb-3">Cluster Articles ({activeCluster.clusters?.length || 0})</h4>
                <div className="space-y-3">
                  {activeCluster.clusters?.map((sub, i) => (
                    <div key={i} className="p-4 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-coral)] transition-colors" data-testid={`cluster-subtopic-${i}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-sm font-medium flex-1">{sub.title}</h5>
                        <span className={`text-[8px] uppercase px-2 py-0.5 rounded-full font-bold ${sub.priority === 'high' ? 'bg-red-50 text-red-600' : sub.priority === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{sub.priority}</span>
                      </div>
                      <p className="text-[10px] text-[var(--color-muted)] mb-2">{sub.description}</p>
                      <div className="flex flex-wrap gap-2 text-[9px] text-[var(--color-muted)]">
                        <span>Keyword: <strong>{sub.target_keyword}</strong></span>
                        <span>&middot;</span>
                        <span>Intent: {sub.search_intent}</span>
                        <span>&middot;</span>
                        <span>~{sub.word_count} words</span>
                        <span>&middot;</span>
                        <span>Link anchor: "{sub.internal_link_anchor}"</span>
                      </div>
                    </div>
                  ))}
                </div>

                {activeCluster.linking_strategy && (
                  <div className="mt-5 p-4 rounded-xl bg-[var(--color-surface)]">
                    <h5 className="text-xs font-medium mb-1">Linking Strategy</h5>
                    <p className="text-[10px] text-[var(--color-muted)]">{activeCluster.linking_strategy}</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <Network className="w-10 h-10 mx-auto mb-4 text-[var(--color-muted)]" />
            <p className="text-sm text-[var(--color-muted)]">Generate or select a cluster to view the strategy</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== SOCIAL POSTS PANEL ==============
const SocialPostsPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [content, setContent] = useState('');
  const [articleId, setArticleId] = useState('');
  const [articles, setArticles] = useState([]);
  const [tone, setTone] = useState('professional');
  const [platforms, setPlatforms] = useState(['twitter', 'linkedin', 'instagram']);
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    axios.get(`${API}/articles`, { headers: getAuthHeaders() }).then(res => setArticles(res.data)).catch(() => {});
  }, []);

  const togglePlatform = (p) => {
    setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const generate = async () => {
    if ((!content.trim() && !articleId) || generating || platforms.length === 0) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/tools/social-posts`, {
        content: content || '', article_id: articleId, platforms, tone
      }, { headers: getAuthHeaders() });
      setPosts(res.data.posts?.posts || res.data.posts);
    } catch (err) {
      alert(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const copyPost = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const PLATFORM_CONFIG = {
    twitter: { label: 'X / Twitter', icon: Twitter, color: 'text-[#1DA1F2]', limit: 280 },
    linkedin: { label: 'LinkedIn', icon: Linkedin, color: 'text-[#0077B5]', limit: 3000 },
    instagram: { label: 'Instagram', icon: Instagram, color: 'text-[#E4405F]', limit: 2200 },
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="social-posts-panel">
      <div className="space-y-4">
        <div className="card p-5">
          <h3 className="font-display text-sm font-medium mb-3">Generate Social Posts</h3>

          <label className="text-[10px] text-[var(--color-muted)] block mb-1">From article:</label>
          <select value={articleId} onChange={(e) => { setArticleId(e.target.value); if (e.target.value) setContent(''); }} className="input w-full mb-3 text-xs" data-testid="social-article-select">
            <option value="">Select an article (or type content below)</option>
            {articles.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>

          <label className="text-[10px] text-[var(--color-muted)] block mb-1">Or paste content:</label>
          <textarea value={content} onChange={(e) => { setContent(e.target.value); if (e.target.value) setArticleId(''); }} placeholder="Paste your content here..." className="input w-full mb-3 text-xs" rows={4} disabled={!!articleId} data-testid="social-content-input" />

          <label className="text-[10px] text-[var(--color-muted)] block mb-1">Platforms:</label>
          <div className="flex gap-2 mb-3">
            {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button key={key} onClick={() => togglePlatform(key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors ${platforms.includes(key) ? 'border-[var(--color-charcoal)] bg-[var(--color-charcoal)] text-white' : 'border-[var(--color-border)]'}`} data-testid={`social-toggle-${key}`}>
                  <Icon className="w-3.5 h-3.5" /> {cfg.label}
                </button>
              );
            })}
          </div>

          <label className="text-[10px] text-[var(--color-muted)] block mb-1">Tone:</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="input w-full mb-3 text-xs">
            <option value="professional">Professional</option>
            <option value="casual">Casual & Friendly</option>
            <option value="bold">Bold & Provocative</option>
            <option value="educational">Educational</option>
          </select>

          <button onClick={generate} disabled={generating || (!content.trim() && !articleId) || platforms.length === 0} className="btn btn-primary w-full text-xs" data-testid="generate-social-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4" /> Generate Posts (1 credit)</>}
          </button>
        </div>
      </div>

      <div data-testid="social-results">
        {posts ? (
          <div className="space-y-4">
            {Object.entries(posts).map(([platform, post]) => {
              const cfg = PLATFORM_CONFIG[platform];
              if (!cfg || !post) return null;
              const Icon = cfg.icon;
              const text = post.text || '';
              const hashtags = post.hashtags || [];
              return (
                <div key={platform} className="card p-5" data-testid={`social-post-${platform}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                    <span className="text-xs font-medium">{cfg.label}</span>
                    <span className="text-[9px] text-[var(--color-muted)] ml-auto">{text.length}/{cfg.limit} chars</span>
                  </div>
                  <div className="p-4 rounded-xl bg-[var(--color-surface)] text-xs leading-relaxed whitespace-pre-wrap mb-3">{text}</div>
                  {hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {hashtags.map((tag, i) => <span key={i} className="text-[9px] text-[var(--color-coral)]">#{tag}</span>)}
                    </div>
                  )}
                  <button onClick={() => copyPost(text + (hashtags.length ? '\n\n' + hashtags.map(t => '#' + t).join(' ') : ''))} className="btn btn-secondary text-xs w-full" data-testid={`copy-social-${platform}`}>
                    {copied === (text + (hashtags.length ? '\n\n' + hashtags.map(t => '#' + t).join(' ') : '')) ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Post</>}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <Share2 className="w-10 h-10 mx-auto mb-4 text-[var(--color-muted)]" />
            <p className="text-sm text-[var(--color-muted)]">Generate social posts from your content or articles</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentTools;
